import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../api/client'
import { useAdminUi } from './AdminUiProvider'
import type {
  AdminOverview,
  AdminPendingVerification,
  AdminSection,
  AdminStuckTransaction,
} from './adminTypes'

type Props = {
  token: string
  overview: AdminOverview | null
  onNavigate: (section: AdminSection, hint?: string) => void
  onOpenTransaction: (id: number) => void
  onOpenUser: (userId: number) => void
}

export default function AdminModerationQueue({
  token,
  overview,
  onNavigate,
  onOpenTransaction,
  onOpenUser,
}: Props) {
  const { t } = useTranslation()
  const { toast, prompt } = useAdminUi()
  const [pending, setPending] = useState<AdminPendingVerification[]>([])
  const [stuck, setStuck] = useState<AdminStuckTransaction[]>([])
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    Promise.allSettled([
      adminAPI.pendingVerifications(token),
      adminAPI.stuckTransactions(token, { limit: 8 }),
    ]).then(([verRes, stuckRes]) => {
      if (verRes.status === 'fulfilled') {
        setPending((verRes.value.data as AdminPendingVerification[]) ?? [])
      }
      if (stuckRes.status === 'fulfilled') {
        setStuck((stuckRes.value.data?.items as AdminStuckTransaction[]) ?? [])
      } else {
        setStuck([])
      }
      const failed = [verRes, stuckRes].find((row) => row.status === 'rejected') as
        | PromiseRejectedResult
        | undefined
      if (failed) {
        const err = failed.reason as { response?: { data?: { detail?: string } }; message?: string }
        setError(err.response?.data?.detail || err.message || 'Request failed')
      }
    })
  }, [token])

  useEffect(() => {
    load()
  }, [load])

  function approve(userId: number) {
    setBusyId(userId)
    adminAPI
      .approveVerification(token, userId)
      .then(() => {
        toast.success(t('admin.toast.approved'))
        load()
      })
      .catch((err) => toast.error(err.response?.data?.detail || err.message))
      .finally(() => setBusyId(null))
  }

  async function reject(userId: number) {
    const note = await prompt({
      title: t('admin.reject'),
      message: t('admin.rejectNotePrompt'),
      placeholder: t('admin.rejectNotePrompt'),
    })
    if (note === null) return
    setBusyId(userId)
    adminAPI
      .rejectVerification(token, userId, { note: note || undefined })
      .then(() => {
        toast.success(t('admin.toast.rejected'))
        load()
      })
      .catch((err) => toast.error(err.response?.data?.detail || err.message))
      .finally(() => setBusyId(null))
  }

  const greedyCount = overview?.listings_above_ai_guidance ?? 0

  return (
    <section className="vc-admin-moderation">
      <h3>{t('admin.moderationQueue')}</h3>
      {error ? <p className="vc-admin-error">{error}</p> : null}
      <div className="vc-admin-moderation__grid">
        <article className="vc-admin-moderation-card">
          <header>
            <span>{t('admin.pendingVerifications')}</span>
            <strong>{pending.length}</strong>
          </header>
          {pending.length === 0 ? (
            <p className="vc-admin-muted">{t('admin.queueClear')}</p>
          ) : (
            <ul className="vc-admin-moderation-list">
              {pending.slice(0, 4).map((row) => (
                <li key={row.user_id}>
                  <button
                    type="button"
                    className="vc-admin-moderation-link"
                    onClick={() => onOpenUser(row.user_id)}
                  >
                    <strong>{row.full_name || row.email}</strong>
                    <span className="vc-admin-muted">{row.email}</span>
                  </button>
                  <div className="vc-admin-moderation-card__actions">
                    <button
                      type="button"
                      className="vc-admin-btn vc-admin-btn--primary"
                      disabled={busyId === row.user_id}
                      onClick={() => approve(row.user_id)}
                    >
                      {t('admin.approve')}
                    </button>
                    <button
                      type="button"
                      className="vc-admin-btn vc-admin-btn--ghost"
                      disabled={busyId === row.user_id}
                      onClick={() => reject(row.user_id)}
                    >
                      {t('admin.reject')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="vc-admin-moderation-card">
          <header>
            <span>{t('admin.stuckTransactions')}</span>
            <strong>{stuck.length}</strong>
          </header>
          {stuck.length === 0 ? (
            <p className="vc-admin-muted">{t('admin.queueClear')}</p>
          ) : (
            <ul className="vc-admin-moderation-list">
              {stuck.slice(0, 4).map((row) => (
                <li key={row.transaction_id}>
                  <button
                    type="button"
                    className="vc-admin-moderation-link"
                    onClick={() => onOpenTransaction(row.transaction_id)}
                  >
                    <strong>#{row.transaction_id}</strong>
                    <span className="vc-admin-muted">{t(`admin.stuckFlag.${row.flag}`)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {stuck.length > 0 ? (
            <button
              type="button"
              className="vc-admin-btn vc-admin-btn--primary vc-admin-moderation-card__cta"
              onClick={() => onNavigate('transactions', 'stuck')}
            >
              {t('admin.viewAllTransactions')}
            </button>
          ) : null}
        </article>

        <article className="vc-admin-moderation-card vc-admin-moderation-card--warn">
          <header>
            <span>{t('admin.aboveAiGuidance')}</span>
            <strong>{greedyCount}</strong>
          </header>
          <p className="vc-admin-muted">{t('admin.aboveAiHint')}</p>
          {greedyCount > 0 ? (
            <button
              type="button"
              className="vc-admin-btn vc-admin-btn--primary vc-admin-moderation-card__cta"
              onClick={() => onNavigate('listings', 'above_ai')}
            >
              {t('admin.reviewListings')}
            </button>
          ) : null}
        </article>
      </div>
    </section>
  )
}
